import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Mail, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function NewsletterSubscribe({ variant = 'full', className = '' }) {
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [preferences, setPreferences] = useState({
    grants: true,
    events: true,
    resources: true,
    blog: true,
    emergency_alerts: true,
    weekly_digest: true
  });

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email, 
          full_name: fullName || null,
          preferences 
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        setSubscribed(true);
        toast.success('Successfully subscribed to the DowUrk newsletter!');
      } else {
        toast.error(data.detail || 'Failed to subscribe');
      }
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const togglePreference = (key) => {
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Compact inline variant
  if (variant === 'inline') {
    if (subscribed) {
      return (
        <div className={`flex items-center gap-2 text-green-600 ${className}`}>
          <CheckCircle className="h-5 w-5" />
          <span className="text-sm font-medium">Subscribed!</span>
        </div>
      );
    }

    return (
      <form onSubmit={handleSubscribe} className={`flex gap-2 ${className}`} data-testid="newsletter-inline">
        <Input
          type="email"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="max-w-xs"
          data-testid="newsletter-email-inline"
        />
        <Button type="submit" disabled={loading} className="bg-[#006847] hover:bg-[#005a3c]">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Subscribe'}
        </Button>
      </form>
    );
  }

  // Compact card variant
  if (variant === 'compact') {
    if (subscribed) {
      return (
        <Card className={`bg-green-50 border-green-200 ${className}`}>
          <CardContent className="p-6 text-center">
            <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-3" />
            <h3 className="font-bold text-green-800">You're Subscribed!</h3>
            <p className="text-sm text-green-600">Check your inbox for a welcome email.</p>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card className={`bg-gradient-to-br from-[#006847] to-[#004530] text-white ${className}`} data-testid="newsletter-compact">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <Mail className="h-8 w-8 text-[#A4D65E]" />
            <div>
              <h3 className="font-bold">Stay Updated</h3>
              <p className="text-sm text-white/70">Weekly opportunities & resources</p>
            </div>
          </div>
          <form onSubmit={handleSubscribe} className="space-y-3">
            <Input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
              data-testid="newsletter-email-compact"
            />
            <Button 
              type="submit" 
              disabled={loading}
              className="w-full bg-[#A4D65E] text-black hover:bg-[#93c24e]"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Subscribe
            </Button>
          </form>
        </CardContent>
      </Card>
    );
  }

  // Full variant with preferences
  if (subscribed) {
    return (
      <Card className={`bg-green-50 border-green-200 ${className}`}>
        <CardContent className="p-8 text-center">
          <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-green-800 mb-2">Welcome to the DowUrk Community!</h3>
          <p className="text-green-600 mb-4">You've been subscribed to our newsletter.</p>
          <p className="text-sm text-gray-600">
            Check your inbox for a welcome email with resources to help you get started.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className} data-testid="newsletter-full">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-3 bg-[#006847]/10 rounded-lg">
            <Mail className="h-6 w-6 text-[#006847]" />
          </div>
          <div>
            <CardTitle>Subscribe to Our Newsletter</CardTitle>
            <CardDescription>
              Get weekly funding opportunities, event updates, and resources
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubscribe} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Email Address *</label>
              <Input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="newsletter-email-full"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Full Name (Optional)</label>
              <Input
                type="text"
                placeholder="Your name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                data-testid="newsletter-name-full"
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium mb-3 block">What would you like to receive?</label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                { key: 'grants', label: 'Grants & Funding', color: 'bg-green-100 text-green-800' },
                { key: 'events', label: 'Events & Workshops', color: 'bg-blue-100 text-blue-800' },
                { key: 'resources', label: 'New Resources', color: 'bg-purple-100 text-purple-800' },
                { key: 'blog', label: 'Blog & Podcast', color: 'bg-pink-100 text-pink-800' },
                { key: 'emergency_alerts', label: 'Emergency Alerts', color: 'bg-red-100 text-red-800' },
                { key: 'weekly_digest', label: 'Weekly Digest', color: 'bg-amber-100 text-amber-800' }
              ].map((pref) => (
                <div 
                  key={pref.key}
                  className={`flex items-center space-x-2 p-3 rounded-lg border cursor-pointer transition ${
                    preferences[pref.key] ? pref.color + ' border-transparent' : 'bg-white border-gray-200 hover:bg-gray-50'
                  }`}
                  onClick={() => togglePreference(pref.key)}
                >
                  <Checkbox 
                    checked={preferences[pref.key]} 
                    onCheckedChange={() => togglePreference(pref.key)}
                  />
                  <span className="text-sm font-medium">{pref.label}</span>
                </div>
              ))}
            </div>
          </div>

          <Button 
            type="submit" 
            disabled={loading}
            className="w-full bg-[#006847] hover:bg-[#005a3c]"
            size="lg"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Subscribing...
              </>
            ) : (
              <>
                <Mail className="h-4 w-4 mr-2" />
                Subscribe to Newsletter
              </>
            )}
          </Button>

          <p className="text-xs text-gray-500 text-center">
            We respect your privacy. Unsubscribe anytime with one click.
          </p>
        </form>
      </CardContent>
    </Card>
  );
}

export default NewsletterSubscribe;
